import "swiper/css/navigation";
import "./slider-reviews.scss";
import "swiper/css";

import RightArrowIcon from "@frontend/images/icons/RightArrowIcon";
import LeftArrowIcon from "@frontend/images/icons/LeftArrowIcon";
import { Navigation, A11y, Autoplay } from "swiper/modules";
import DefaultImage from "@frontend/images/DefaultImage";
import { Swiper, SwiperSlide } from "swiper/react";
import { useMemo, useRef, useState } from "react";
import useT from "@frontend/js/hooks/useT";


export default function SliderReviews({ reviews = [], className = "" }) {
    const prevRef = useRef(null);
    const nextRef = useRef(null);
    const [brokenAvatars, setBrokenAvatars] = useState({});
    const { t } = useT();

    const normalizedReviews = useMemo(() => {
        const sourceReviews =
            Array.isArray(reviews) && reviews.length > 0 ? reviews : [];

        return sourceReviews.filter(Boolean);
    }, [reviews]);
    const hasMultipleSlides = normalizedReviews.length > 1;

    return (
        <div className={`sliderReviews ${className}`.trim()}>
            <div className="sliderReviews__header">
                <h2>{t("reviews-title")}</h2>
                {hasMultipleSlides && (
                    <div className="sliderReviews__header__navigation">
                        <button
                            ref={prevRef}
                            type="button"
                            className="sliderReviews__header__navigation__arrow"
                            aria-label="Previous review"
                        >
                            <LeftArrowIcon />
                        </button>
                        <button
                            ref={nextRef}
                            type="button"
                            className="sliderReviews__header__navigation__arrow"
                            aria-label="Next review"
                        >
                            <RightArrowIcon />
                        </button>
                    </div>
                )}
            </div>

            <Swiper
                modules={[Navigation, A11y, Autoplay]}
                slidesPerView={1}
                spaceBetween={16}
                loop={hasMultipleSlides}
                autoplay={
                    hasMultipleSlides
                        ? {
                              delay: 15000,
                              disableOnInteraction: false,
                              pauseOnMouseEnter: true,
                          }
                        : false
                }
                navigation={
                    hasMultipleSlides
                        ? {
                              prevEl: prevRef.current,
                              nextEl: nextRef.current,
                          }
                        : false
                }
                onBeforeInit={(swiper) => {
                    if (!hasMultipleSlides) {
                        return;
                    }

                    swiper.params.navigation.prevEl = prevRef.current;
                    swiper.params.navigation.nextEl = nextRef.current;
                }}
                className="sliderReviews__swiper"
            >
                {normalizedReviews.map((review, index) => {
                    const firstName = `${review?.firstName || ""}`.trim();
                    const lastName = `${review?.lastName || ""}`.trim();
                    const fullName =
                        `${review?.name || `${firstName} ${lastName}`}`.trim();
                    const slideKey = review?.id || fullName || index;
                    const hasAvatar = Boolean(review?.avatar);
                    const isAvatarBroken = Boolean(brokenAvatars[slideKey]);
                    const shouldShowAvatar = hasAvatar && !isAvatarBroken;

                    return (
                        <SwiperSlide key={slideKey}>
                            <div className="sliderReviews__swiper__slide">
                                <div className="sliderReviews__swiper__slide__header">
                                    <div
                                        className="sliderReviews__swiper__slide__header__avatar"
                                        aria-hidden="true"
                                    >
                                        {shouldShowAvatar ? (
                                            <img
                                                src={review.avatar}
                                                alt={fullName}
                                                loading="lazy"
                                                onError={() => {
                                                    setBrokenAvatars(
                                                        (previousState) => ({
                                                            ...previousState,
                                                            [slideKey]: true,
                                                        }),
                                                    );
                                                }}
                                            />
                                        ) : (
                                            <div className="sliderReviews__swiper__slide__header__avatar__fallback">
                                                <DefaultImage />
                                            </div>
                                        )}
                                    </div>

                                    <div className="sliderReviews__swiper__slide__header__identity">
                                        <h4>{fullName}</h4>
                                        <span>{review?.position}</span>
                                        <span>{review?.company}</span>
                                    </div>
                                </div>

                                <p className="sliderReviews__swiper__slide__text">
                                    {(review?.text || "")
                                        .split(/(?<=\.)\s+/)
                                        .map((sentence, i, arr) => (
                                            <span key={i}>
                                                {sentence}
                                                {i < arr.length - 1 && <br />}
                                            </span>
                                        ))}
                                </p>
                            </div>
                        </SwiperSlide>
                    );
                })}
            </Swiper>
        </div>
    );
}
